import { useState, useEffect } from "react";
import { GamePlaceholder } from "@/components/ui/GamePlaceholder";

interface GameImageProps {
  src?: string | null;
  alt: string;
  className?: string;
  loading?: "lazy" | "eager";
  // Applied to the fallback placeholder (e.g. "aspect-square" so it keeps its
  // height in a container that has no intrinsic size).
  placeholderClassName?: string;
}

// Renders a game image, falling back to the GamePlaceholder both when there's
// no URL AND when the URL fails to load (e.g. a shop deleted the product and
// its CDN now 404s). Without the onError fallback a dead-but-present URL shows
// the browser's broken-image icon, which makes the site look abandoned.
export function GameImage({
  src,
  alt,
  className = "",
  loading,
  placeholderClassName = "",
}: GameImageProps) {
  const [failed, setFailed] = useState(false);

  // Cards are reused across list items as data changes; reset on a new src so a
  // previously-failed slot can render a fresh, working image.
  useEffect(() => setFailed(false), [src]);

  if (!src || failed)
    return <GamePlaceholder className={placeholderClassName} />;

  return (
    <img
      src={src}
      alt={alt}
      loading={loading}
      className={className}
      onError={() => setFailed(true)}
    />
  );
}
